import { GetProviderInvoicesResponse, ProviderInvoice, SubscriptionBillingHistoryItem } from "./types";

/**
 * Maps backend invoice status to a user-friendly display status
 */
const _mapInvoiceStatusToDisplay = (invoice: ProviderInvoice): string => {
  // Completed status means paid for subscriptions
  if (invoice.completedAt || invoice.status === "Completed") {
    return "Paid";
  }
  if (invoice.status === "Active") {
    return "Active";
  }
  if (invoice.status === "PastDue" || invoice.isPastDue) {
    return "Past Due";
  }
  if (invoice.status === "Refunded") {
    return "Refunded";
  }
  if (invoice.status === "Cancelled") {
    return "Cancelled";
  }
  if (invoice.status === "Voided") {
    return "Voided";
  }
  if (invoice.status === "Finalized") {
    return "Pending";
  }
  return invoice.status;
};

/**
 * Transforms a ProviderInvoice to a SubscriptionBillingHistoryItem
 * Note: planName defaults to "Subscription" - to get the actual plan name,
 * the subscription data needs to be fetched separately using metadata.subscriptionPlanId
 */
export const transformInvoiceToBillingHistoryItem = (
  invoice: ProviderInvoice,
  planNameOverride?: string
): SubscriptionBillingHistoryItem => {
  // Find the invoice document URL from the documents array
  const invoiceDocument = invoice.documents?.find((doc) => doc.entityType === "Invoice");

  return {
    id: invoice.salesOrderId,
    // Use plan name override if provided, otherwise use a default
    // The actual plan name can be looked up using metadata.subscriptionPlanId
    planName: planNameOverride || "Subscription Plan",
    // Use finalizedAt as the invoice date, fall back to createdAt
    date: invoice.finalizedAt || invoice.createdAt,
    // Keep amount in cents - formatCurrency utility handles conversion to dollars
    amount: invoice.orderTotal,
    currency: invoice.currency,
    status: invoice.status,
    documentUrl: invoiceDocument?.fileUrl,
  };
};

/**
 * Transforms the full provider invoices response to billing history items
 */
export const transformProviderInvoicesToBillingHistory = (response: GetProviderInvoicesResponse): SubscriptionBillingHistoryItem[] => {
  return response.data.map((invoice) => {
    const planName = invoice.metadata?.notes ?? undefined;
    return transformInvoiceToBillingHistoryItem(invoice, planName);
  });
};
